import React, { useState, useEffect, useRef } from 'react';

// ─── Chart constants ───────────────────────────────────────────────────────
const CHART = { w: 520, h: 380, pad: { top: 44, right: 36, bottom: 52, left: 64 } };
const CW = CHART.w - CHART.pad.left - CHART.pad.right;
const CH = CHART.h - CHART.pad.top - CHART.pad.bottom;

const X_MIN = 140, X_MAX = 200;
const Y_MIN = 80,   Y_MAX = 140;

function toX(margin) { return CHART.pad.left + ((margin - X_MIN) / (X_MAX - X_MIN)) * CW; }
function toY(balance) { return CHART.pad.top + ((Y_MAX - balance) / (Y_MAX - Y_MIN)) * CH; }

function frontierMargin(bal) {
  const t = (bal - Y_MIN) / (Y_MAX - Y_MIN);
  return 198 - 50 * Math.pow(t, 1.5);
}

const curveData = [];
for (let i = 0; i <= 100; i++) {
  const bal = Y_MIN + (i / 100) * (Y_MAX - Y_MIN);
  curveData.push({ bal, margin: frontierMargin(bal) });
}
const curvePath = curveData
  .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.margin).toFixed(1)},${toY(p.bal).toFixed(1)}`)
  .join(' ');
const areaPath = `${curvePath} L${toX(X_MIN).toFixed(1)},${toY(Y_MAX).toFixed(1)} L${toX(X_MIN).toFixed(1)},${toY(Y_MIN).toFixed(1)} Z`;

const CURRENT = { bal: 110, margin: 165 };
const FRONTIER_AT_CURRENT = frontierMargin(CURRENT.bal);

const xTicks = [140, 150, 160, 170, 180, 190, 200];
const yTicks = [80, 90, 100, 110, 120, 130, 140];

// ─── Default product data (savings) ─────────────────────────────────────────
const DEFAULT_PRODUCTS = [
  { name: 'Easy Access',    balance: 35, currentRate: 3.20, optimisedRate: 3.00 },
  { name: '1 Year Fixed',   balance: 20, currentRate: 4.50, optimisedRate: 4.25 },
  { name: '2 Year Fixed',   balance: 25, currentRate: 4.80, optimisedRate: 4.60 },
  { name: 'Notice',         balance: 15, currentRate: 3.75, optimisedRate: 3.55 },
  { name: 'Regular Saver',  balance: 15, currentRate: 5.00, optimisedRate: 4.80 },
];
const CURRENT_NIM = 165;
const OPTIMISED_NIM = Math.round(frontierMargin(CURRENT.bal));

// ─── Easing ────────────────────────────────────────────────────────────────
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function mapRange(val, inMin, inMax, outMin, outMax) {
  const t = Math.max(0, Math.min(1, (val - inMin) / (inMax - inMin)));
  return outMin + easeInOut(t) * (outMax - outMin);
}

const FONT = "'Inter','Segoe UI',sans-serif";
const MOBILE_BP = 900;

// ─── Main component ─────────────────────────────────────────────────────────
export default function EfficientFrontierSection({ products }) {
  const PRODUCTS = products || DEFAULT_PRODUCTS;
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const contentRowRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const wrapper = wrapperRef.current;
      const sticky = stickyRef.current;
      const contentRow = contentRowRef.current;
      if (!wrapper || !sticky || !contentRow) return;

      const rect = wrapper.getBoundingClientRect();
      const totalScroll = wrapper.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;

      // Start the interactive sequence as soon as the bottom of the core
      // chart/table row becomes visible in the viewport.
      const rowBottomWithinWrapper = sticky.offsetTop + contentRow.offsetTop + contentRow.offsetHeight;
      const startLeadPx = Math.min(220, Math.max(100, window.innerHeight * 0.18));
      const startScroll = rowBottomWithinWrapper - window.innerHeight - startLeadPx;
      
      // Calculate the total available scroll distance for the sticky section
      const availableScroll = Math.max(1, totalScroll - startScroll);
      
      // Set the animation to finish after 70% of the scroll, leaving 30% as a "hold"
      const interactiveScrollRange = availableScroll * 0.7;
      
      const p = Math.max(0, Math.min(1, (scrolled - startScroll) / interactiveScrollRange));
      setProgress(p);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BP);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ─── Animated values ───────────────────────────────────────────────────
  const optimisedColOpacity = mapRange(progress, 0.25, 0.45, 0, 1);
  const gainOpacity = mapRange(progress, 0.7, 0.9, 0, 1);

  const pulseOpacity = mapRange(progress, 0.05, 0.2, 0, 1) * (1 - mapRange(progress, 0.2, 0.3, 0, 1));
  const pulseRadius = mapRange(progress, 0.05, 0.3, 6, 22);

  const animatedMargin = mapRange(progress, 0.4, 0.7, CURRENT.margin, FRONTIER_AT_CURRENT);
  const dotX = toX(animatedMargin);
  const dotYPos = toY(CURRENT.bal);
  const originX = toX(CURRENT.margin);
  const frontierX = toX(FRONTIER_AT_CURRENT);

  const labelOpacity = mapRange(progress, 0.7, 0.9, 0, 1);
  const unrealisedGain = Math.round(animatedMargin - CURRENT.margin);
  const isOnFrontier = progress > 0.7;

  const dashOpacity = mapRange(progress, 0.4, 0.55, 0, 0.7);

  // ─── Responsive style values ──────────────────────────────────────────
  const m = isMobile;
  const thStyle = (align, color) => ({
    textAlign: align,
    padding: m ? '4px 4px 8px' : '8px 8px 12px',
    color, fontWeight: 500,
    fontSize: m ? '9px' : '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  });
  const tdPad = m ? '5px 4px' : '10px 8px';
  const tdFontSize = m ? '11px' : '13px';

  return (
    // Increased height to allow for longer scroll duration
    <div ref={wrapperRef} style={{ height: m ? '200vh' : '400vh', position: 'relative' }}>
      <div ref={stickyRef} style={{
        position: 'sticky', top: 0, height: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: m ? 'flex-start' : 'center',
        background: '#f8fafc',
        padding: m ? '80px 10px 0' : '0 24px',
        boxSizing: 'border-box',
      }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: m ? '10px' : '32px' }}>
          <h2 style={{ fontFamily: FONT, fontSize: m ? '20px' : '28px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
            Your efficient frontier
          </h2>
          <p style={{ fontFamily: FONT, fontSize: m ? '13px' : '16px', color: '#64748b', maxWidth: '560px', margin: 0 }}>
            See how each rate decision affects your blended margin — and where the opportunity sits.
          </p>
        </div>

        {/* Two-column (desktop) / stacked (mobile) layout */}
        <div ref={contentRowRef} style={{
          display: 'flex',
          flexDirection: m ? 'column' : 'row',
          gap: m ? '10px' : '24px',
          maxWidth: m ? '100%' : '1060px',
          width: '100%',
          alignItems: m ? 'stretch' : 'flex-start',
        }}>

          {/* ── Rates table ── */}
          <div style={{
            ...(m
              ? { width: '100%', maxWidth: '420px', alignSelf: 'center' }
              : { width: '420px', minWidth: '420px' }),
            background: '#fff', borderRadius: m ? '10px' : '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            padding: m ? '10px' : '24px',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: tdFontSize }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                  <th style={thStyle('left', '#64748b')}>Product</th>
                  <th style={thStyle('right', '#64748b')}>Bal $bn</th>
                  <th style={thStyle('right', '#dc2626')}>Current</th>
                  <th style={thStyle('right', '#2563eb')}>Optimised</th>
                </tr>
              </thead>
              <tbody>
                {PRODUCTS.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: tdPad, color: '#0f172a', fontWeight: 500 }}>{p.name}</td>
                    <td style={{ padding: tdPad, textAlign: 'right', color: '#64748b' }}>{p.balance.toFixed(0)}</td>
                    <td style={{ padding: tdPad, textAlign: 'right', fontFamily: 'monospace', color: '#0f172a' }}>{p.currentRate.toFixed(2)}%</td>
                    <td style={{ padding: tdPad, textAlign: 'right', fontFamily: 'monospace', color: '#2563eb', fontWeight: 600, opacity: optimisedColOpacity, transition: 'opacity 0.4s' }}>
                      {p.optimisedRate.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                  <td style={{ padding: m ? '8px 4px' : '12px 8px', fontWeight: 700, color: '#0f172a', fontSize: tdFontSize }}>Blended NIM</td>
                  <td style={{ padding: m ? '8px 4px' : '12px 8px', textAlign: 'right', color: '#64748b' }}>$110bn</td>
                  <td style={{ padding: m ? '8px 4px' : '12px 8px', textAlign: 'right' }}>
                    <span style={{ background: '#fef2f2', color: '#dc2626', padding: m ? '2px 6px' : '3px 8px', borderRadius: '4px', fontFamily: 'monospace', fontWeight: 700, fontSize: m ? '10px' : '12px' }}>{CURRENT_NIM} bps</span>
                  </td>
                  <td style={{ padding: m ? '8px 4px' : '12px 8px', textAlign: 'right', opacity: optimisedColOpacity, transition: 'opacity 0.4s' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', padding: m ? '2px 6px' : '3px 8px', borderRadius: '4px', fontFamily: 'monospace', fontWeight: 700, fontSize: m ? '10px' : '12px' }}>{OPTIMISED_NIM} bps</span>
                  </td>
                </tr>
                {progress > 0.65 && (
                  <tr>
                    <td colSpan={4} style={{ padding: m ? '6px 4px 2px' : '8px 6px 4px', textAlign: 'right', opacity: gainOpacity, transition: 'opacity 0.3s' }}>
                      <span style={{
                        display: 'inline-block', background: '#f0fdf4', color: '#16a34a',
                        border: '1px solid #bbf7d0', padding: m ? '3px 8px' : '4px 12px', borderRadius: '4px',
                        fontFamily: 'monospace', fontWeight: 700, fontSize: m ? '11px' : '13px',
                      }}>
                        +{OPTIMISED_NIM - CURRENT_NIM} bps uplift
                      </span>
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>

          {/* ── Chart ── */}
          <div style={{
            flex: m ? 'none' : 1,
            width: m ? '100%' : undefined,
            maxWidth: m ? '420px' : undefined,
            alignSelf: m ? 'center' : undefined,
            background: '#fff', borderRadius: m ? '10px' : '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            padding: m ? '10px' : '24px',
          }}>
            <svg
              viewBox={`0 0 ${CHART.w} ${CHART.h}`}
              xmlns="https://www.w3.org/2000/svg"
              style={{ width: '100%', display: 'block' }}
            >
              <defs>
                <linearGradient id="efGrad" x1="1" y1="0" x2="0" y2="0">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity="0.04" />
                </linearGradient>
              </defs>

              <rect x="0" y="0" width={CHART.w} height={CHART.h} fill="#fff" />

              {/* Grid */}
              {yTicks.map(v => (
                <line key={`yg-${v}`} x1={CHART.pad.left} y1={toY(v)} x2={CHART.pad.left + CW} y2={toY(v)} stroke="#f1f5f9" strokeWidth="1" />
              ))}
              {xTicks.map(v => (
                <line key={`xg-${v}`} x1={toX(v)} y1={CHART.pad.top} x2={toX(v)} y2={CHART.pad.top + CH} stroke="#f1f5f9" strokeWidth="1" />
              ))}

              {/* Axes */}
              <line x1={CHART.pad.left} y1={CHART.pad.top + CH} x2={CHART.pad.left + CW} y2={CHART.pad.top + CH} stroke="#cbd5e1" strokeWidth="1" />
              <line x1={CHART.pad.left} y1={CHART.pad.top} x2={CHART.pad.left} y2={CHART.pad.top + CH} stroke="#cbd5e1" strokeWidth="1" />

              {/* X-axis ticks */}
              {xTicks.map(v => (
                <g key={`xt-${v}`}>
                  <line x1={toX(v)} y1={CHART.pad.top + CH} x2={toX(v)} y2={CHART.pad.top + CH + 5} stroke="#cbd5e1" strokeWidth="1" />
                  <text x={toX(v)} y={CHART.pad.top + CH + 22} textAnchor="middle" fontFamily={FONT} fontSize="13" fill="#94a3b8">{v}</text>
                </g>
              ))}
              <text x={CHART.pad.left + CW / 2} y={CHART.h - 6} textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight="600" fill="#64748b">Net Interest Margin (bps)</text>

              {/* Y-axis ticks */}
              {yTicks.map(v => (
                <g key={`yt-${v}`}>
                  <line x1={CHART.pad.left - 5} y1={toY(v)} x2={CHART.pad.left} y2={toY(v)} stroke="#cbd5e1" strokeWidth="1" />
                  <text x={CHART.pad.left - 10} y={toY(v) + 5} textAnchor="end" fontFamily={FONT} fontSize="13" fill="#94a3b8">{v}</text>
                </g>
              ))}
              <text x={16} y={CHART.pad.top + CH / 2} textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight="600" fill="#64748b" transform={`rotate(-90, 16, ${CHART.pad.top + CH / 2})`}>Balance ($bn)</text>

              {/* Shaded region */}
              <path d={areaPath} fill="url(#efGrad)" />

              {/* Frontier curve */}
              <path d={curvePath} fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

              {/* Legend */}
              <g>
                <line x1={CHART.w - 160} y1={CHART.pad.top + 8} x2={CHART.w - 130} y2={CHART.pad.top + 8} stroke="#2563eb" strokeWidth="3" strokeLinecap="round" />
                <text x={CHART.w - 124} y={CHART.pad.top + 13} fontFamily={FONT} fontSize="13" fontWeight="600" fill="#2563eb">Efficient frontier</text>
              </g>

              {/* Dashed horizontal line */}
              {progress > 0.4 && (
                <line
                  x1={dotX} y1={dotYPos}
                  x2={originX} y2={dotYPos}
                  stroke="#2563eb" strokeWidth="2.5" strokeDasharray="6,4"
                  opacity={dashOpacity}
                />
              )}

              {/* Unrealised margin badge */}
              {progress > 0.65 && (
                <g opacity={labelOpacity}>
                  <rect
                    x={(dotX + originX) / 2 - 62}
                    y={dotYPos + 14}
                    width="124" height="24" rx="4"
                    fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1"
                  />
                  <text
                    x={(dotX + originX) / 2}
                    y={dotYPos + 30}
                    textAnchor="middle"
                    fontFamily={FONT} fontSize="12" fontWeight="700" fill="#2563eb">
                    +{unrealisedGain} bps unrealised
                  </text>
                </g>
              )}

              {/* Pulse ring */}
              {progress > 0.02 && progress < 0.35 && (
                <circle cx={originX} cy={dotYPos} r={pulseRadius}
                  fill="none" stroke="#ef4444" strokeWidth="1.5" opacity={pulseOpacity} />
              )}

              {/* Animated dot */}
              <circle
                cx={dotX} cy={dotYPos} r="7"
                fill={isOnFrontier ? '#2563eb' : '#ef4444'}
                stroke="#fff" strokeWidth="2.5"
                style={{ transition: 'fill 0.4s ease' }}
              />

              {/* Current rates label */}
              <g opacity={1 - mapRange(progress, 0.4, 0.6, 0, 1)}>
                <rect x={originX - 42} y={dotYPos - 32} width="84" height="24" rx="4" fill="#fef2f2" stroke="#fecaca" strokeWidth="1" />
                <text x={originX} y={dotYPos - 16} textAnchor="middle" fontFamily={FONT} fontSize="13" fontWeight="600" fill="#dc2626">Current rates</text>
              </g>

              {/* Optimised label */}
              {progress > 0.65 && (
                <g opacity={labelOpacity}>
                  <rect x={frontierX - 46} y={dotYPos - 32} width="92" height="24" rx="4" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1" />
                  <text x={frontierX} y={dotYPos - 16} textAnchor="middle" fontFamily={FONT} fontSize="13" fontWeight="600" fill="#2563eb">Optimised</text>
                </g>
              )}

              {/* Info icon */}
              <g style={{ cursor: 'pointer' }} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
                <circle cx={CHART.w - 24} cy="24" r="10" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                <text x={CHART.w - 24} y="29" textAnchor="middle" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#94a3b8">i</text>
              </g>
              {showTooltip && (
                <g>
                  <rect x={CHART.w - 260} y="40" width="236" height="40" rx="6" fill="#1e293b" opacity="0.95" />
                  <text x={CHART.w - 254} y="58" fontFamily={FONT} fontSize="11" fill="#fff">Each point on the frontier represents the</text>
                  <text x={CHART.w - 254} y="72" fontFamily={FONT} fontSize="11" fill="#fff">maximum margin achievable at that balance level.</text>
                </g>
              )}
            </svg>
          </div>
        </div>

        {/* Scroll hint */}
        {!m && (
          <p style={{
            marginTop: '24px', fontFamily: FONT, fontSize: '13px', color: '#94a3b8',
            opacity: 1 - mapRange(progress, 0, 0.1, 0, 1), transition: 'opacity 0.3s',
          }}>
            ↓ Scroll to see the opportunity
          </p>
        )}
      </div>
    </div>
  );
}
